import {
  Cpu,
  Plus,
  Search,
  Filter,
  AlertCircle,
  ExternalLink,
  Pencil,
  RefreshCw,
  Trash2,
  Radio,
  Terminal,
} from 'lucide-react'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { openDeviceWebUiWindow } from '../../utils/openDeviceWebUiWindow'
import { openDeviceConsoleWindow } from '../../utils/openDeviceConsoleWindow'
import { useApplications } from '../../hooks/useApplications'
import { useDevices, useDeleteDevice } from '../../hooks/useDevices'
import { useRole } from '../../hooks/useRole'
import DeviceModal from '../../components/Modal/DeviceModal'
import DeviceEditModal from '../../components/Modal/DeviceEditModal'
import { isMqttDeviceKind, MqttEmqxPlataformaCell } from './MqttEmqxPlataformaCell'
import { formatDateTime } from '../../utils/format'

const kindLabels = {
  1: 'LoRaWAN',
  2: 'MQTT',
  3: 'Custom API',
  LoRaWan: 'LoRaWAN',
  Mqtt: 'MQTT',
  CustomApi: 'Custom API',
}

const statusKeyMap = {
  0: 'devices.status.pending',
  NeverSeen: 'devices.status.pending',
  1: 'devices.status.provisioning',
  2: 'devices.status.active',
  3: 'devices.status.warning',
  4: 'devices.status.offline',
  5: 'devices.status.decommissioned',
  Provisioning: 'devices.status.provisioning',
  Active: 'devices.status.active',
  Warning: 'devices.status.warning',
  Offline: 'devices.status.offline',
  Decommissioned: 'devices.status.decommissioned',
}

const statusColorMap = {
  0: 'badge-gray',
  NeverSeen: 'badge-gray',
  1: 'badge-gray',
  2: 'badge-success',
  3: 'badge-warning',
  4: 'badge-error',
  5: 'badge-gray',
  Provisioning: 'badge-gray',
  Active: 'badge-success',
  Warning: 'badge-warning',
  Offline: 'badge-error',
  Decommissioned: 'badge-gray',
}

function kindLabel(k) {
  return kindLabels[k] ?? (k != null ? String(k) : '—')
}

function isCustomApiKind(kind) {
  return kind === 3 || kind === 'CustomApi' || kind === '3'
}

function StatusBadge({ status }) {
  const { t } = useTranslation()
  const key = statusKeyMap[status]
  const color = statusColorMap[status] ?? 'badge-gray'
  const label = key ? t(key) : (status != null ? String(status) : '—')
  return <span className={clsx('badge', color)}>{label}</span>
}

function pick(device, camel, snake) {
  if (device[camel] !== undefined && device[camel] !== null) return device[camel]
  if (snake && device[snake] !== undefined && device[snake] !== null) return device[snake]
  return device[camel] ?? (snake ? device[snake] : undefined)
}

function syncProgressParts(device) {
  const dd = pick(device, 'webDeviceDownloadDone', 'web_device_download_done')
  const dt = pick(device, 'webDeviceDownloadTotal', 'web_device_download_total')
  if (dd != null && dt != null && dt > 0) return { cur: dd, total: dt, kind: 'files' }
  const sc = pick(device, 'webDeviceSyncedFileCount', 'web_device_synced_file_count')
  const tc = pick(device, 'webDeviceTotalFileCount', 'web_device_total_file_count')
  if (sc != null && tc != null && tc > 0) return { cur: sc, total: tc, kind: 'manifest' }
  return null
}

function useBuildWebCloudUi() {
  const { t } = useTranslation()
  return (device) => {
    if (!isCustomApiKind(device.kind)) {
      return { badges: [{ label: '—', color: 'badge-gray' }], detailLines: [] }
    }

    const wdOn = pick(device, 'webDeviceEnabled', 'web_device_enabled')
    const wdTok = pick(device, 'webDeviceTokenConfigured', 'web_device_token_configured')
    if (!wdOn) {
      return {
        badges: [
          { label: t('devices.cloud.remoteOff'), color: 'badge-gray', title: t('devices.cloud.remoteOffHint') },
        ],
        detailLines: [],
      }
    }
    if (!wdTok) {
      return {
        badges: [
          { label: t('devices.cloud.tokenPending'), color: 'badge-warning', title: t('devices.cloud.tokenPendingHint') },
        ],
        detailLines: [],
      }
    }

    const tunnel = pick(device, 'webDeviceTunnelOnline', 'web_device_tunnel_online')
    const syncSt = String(pick(device, 'webDeviceSyncStatus', 'web_device_sync_status') || '')
    const st = syncSt.toLowerCase()
    const telemJson = pick(device, 'webDeviceTelemetryJson', 'web_device_telemetry_json')
    const telemAt = pick(device, 'webDeviceTelemetryReceivedAt', 'web_device_telemetry_received_at')

    const badges = []
    if (tunnel === true) {
      badges.push({ label: t('devices.cloud.online'), color: 'badge-success' })
    } else if (tunnel === false) {
      badges.push({ label: t('devices.cloud.offline'), color: 'badge-gray', title: t('devices.cloud.offlineHint') })
    } else {
      badges.push({ label: t('devices.cloud.unavailable'), color: 'badge-warning', title: t('devices.cloud.unavailableHint') })
    }

    if (tunnel === true) {
      if (st === 'done') {
        badges.push({ label: t('devices.cloud.synced'), color: 'badge-success' })
      } else if (st === 'error') {
        badges.push({ label: t('devices.cloud.error'), color: 'badge-error', title: t('devices.cloud.errorHint') })
      } else {
        const syncingNow = st === 'syncing' || st === 'requesting'
        badges.push({
          key: 'no-sync',
          label: t('devices.cloud.noSync'),
          color: 'badge-warning',
          title: syncingNow ? t('devices.cloud.noSyncHintSyncing') : t('devices.cloud.noSyncHintIdle'),
        })
      }
    }

    const detailLines = []
    const parts = syncProgressParts(device)
    if (parts) {
      detailLines.push(
        parts.kind === 'files'
          ? t('devices.cloud.detail.files', { cur: parts.cur, total: parts.total })
          : t('devices.cloud.detail.manifest', { cur: parts.cur, total: parts.total })
      )
    }

    let telem = null
    try {
      telem = telemJson ? JSON.parse(telemJson) : null
    } catch {
      telem = null
    }
    if (telem && typeof telem === 'object') {
      if (telem.free_heap != null)
        detailLines.push(t('devices.cloud.detail.ram', { kib: Math.round(Number(telem.free_heap) / 1024) }))
      if (telem.heap_frag_pct != null)
        detailLines.push(t('devices.cloud.detail.heapFrag', { value: telem.heap_frag_pct }))
      if (telem.littlefs_used != null && telem.littlefs_total != null) {
        detailLines.push(
          t('devices.cloud.detail.littlefs', {
            used: Math.round(Number(telem.littlefs_used) / 1024),
            total: Math.round(Number(telem.littlefs_total) / 1024),
          })
        )
      }
      if (telem.wifi_rssi != null)
        detailLines.push(t('devices.cloud.detail.wifiRssi', { rssi: telem.wifi_rssi }))
      if (telem.lora_rssi != null)
        detailLines.push(t('devices.cloud.detail.loraRssi', { rssi: telem.lora_rssi }))
    }
    if (telemAt) {
      detailLines.push(
        t('devices.cloud.detail.telemetry', {
          time: formatDateTime(telemAt, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
        })
      )
    }

    return { badges, detailLines }
  }
}

function canOpenWebUi(device) {
  const hasTemplate = !!pick(device, 'templateId', 'template_id')
  if (!hasTemplate) return false
  const wdOn = pick(device, 'webDeviceEnabled', 'web_device_enabled')
  const wdTok = pick(device, 'webDeviceTokenConfigured', 'web_device_token_configured')
  if (!wdOn || !wdTok) return false
  return true
}

function useWebUiDisabledReason() {
  const { t } = useTranslation()
  return (device) => {
    const hasTemplate = !!pick(device, 'templateId', 'template_id')
    if (!hasTemplate) return t('devices.uiDisabledReason.noTemplate')
    const wdOn = pick(device, 'webDeviceEnabled', 'web_device_enabled')
    if (!wdOn) return t('devices.uiDisabledReason.remoteOff')
    const wdTok = pick(device, 'webDeviceTokenConfigured', 'web_device_token_configured')
    if (!wdTok) return t('devices.uiDisabledReason.tokenPending')
    return ''
  }
}

const iconAct =
  'p-2 rounded-lg transition-colors disabled:opacity-40 disabled:pointer-events-none disabled:cursor-not-allowed'

export default function Devices() {
  const { t } = useTranslation()
  const { data: devices, isLoading, error, refetch, isFetching } = useDevices()
  const { data: applications } = useApplications()
  const deleteDevice = useDeleteDevice()
  const { canDelete } = useRole()
  const [searchTerm, setSearchTerm] = useState('')
  const [applicationFilter, setApplicationFilter] = useState('all')
  const [tokenDialog, setTokenDialog] = useState(null)
  const [deviceModalOpen, setDeviceModalOpen] = useState(false)
  const [editDevice, setEditDevice] = useState(null)
  const buildWebCloudUi = useBuildWebCloudUi()
  const webUiDisabledReason = useWebUiDisabledReason()

  const appNameById = Object.fromEntries(
    (applications ?? []).map((a) => [a.id, a.name])
  )

  const list = devices ?? []

  const filteredDevices = list.filter((device) => {
    const matchesSearch =
      device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.devEui?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(device.id).toLowerCase().includes(searchTerm.toLowerCase())
    const appId = device.applicationId ?? device.application_id
    const matchesApplication =
      applicationFilter === 'all' || String(appId) === applicationFilter
    return matchesSearch && matchesApplication
  })

  const stats = {
    total: list.length,
    webOn: list.filter((d) => d.webDeviceEnabled).length,
  }

  /** Atualiza telemetria/sync na tabela sem esperar o refetch automático (~60 s). Não contacta o equipamento. */
  const runRefreshCloudRow = async () => {
    try {
      await refetch()
    } catch (e) {
      alert(e?.message || t('devices.alerts.refreshFailed'))
    }
  }

  const runDelete = async (device) => {
    const id = device?.id
    if (!id) {
      alert(t('devices.alerts.invalidForDelete'))
      return
    }
    const devLabel = device?.name || device?.devEui || id
    if (!window.confirm(t('devices.deleteConfirm', { name: devLabel }))) return
    try {
      await deleteDevice.mutateAsync(id)
      await refetch()
    } catch (e) {
      alert(e.message || t('devices.alerts.deleteFailed'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">{t('devices.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error.message}
        </div>
      </div>
    )
  }

  const canCreateDevice = (applications ?? []).length > 0

  return (
    <div className="space-y-6">
      <DeviceModal
        isOpen={deviceModalOpen}
        onClose={() => setDeviceModalOpen(false)}
        applications={applications ?? []}
      />

      <DeviceEditModal
        device={editDevice}
        isOpen={!!editDevice}
        onClose={() => setEditDevice(null)}
        onTokenIssued={(data) => setTokenDialog(data)}
        onAfterWebDeviceChange={() => refetch()}
        onDevicePatch={(patch) =>
          setEditDevice((d) =>
            d && String(d.id) === String(patch.id) ? { ...d, ...patch } : d
          )
        }
      />

      {tokenDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">{t('devices.tokenDialog.title')}</h3>
            <p className="text-sm text-gray-600">
              {tokenDialog.message || t('devices.tokenDialog.defaultMessage')}
            </p>
            <textarea
              readOnly
              className="w-full font-mono text-xs p-2 border rounded-md bg-gray-50"
              rows={4}
              value={tokenDialog.token}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  navigator.clipboard.writeText(tokenDialog.token)
                }}
              >
                {t('devices.tokenDialog.copy')}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setTokenDialog(null)}>
                {t('devices.tokenDialog.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('devices.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('devices.subtitle')}
            </p>
          </div>
          <div
            className="flex items-center gap-2 text-xs text-gray-500"
            title={t('devices.refreshIntervalHint')}
          >
            <Radio className="w-4 h-4 text-green-600 shrink-0" aria-hidden />
            <span>{t('devices.refreshInterval')}</span>
            {isFetching && !isLoading && (
              <span className="flex items-center gap-1 text-blue-600">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                {t('devices.refreshing')}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canCreateDevice}
          title={canCreateDevice ? t('devices.newCreateHint') : t('devices.alerts.createAppFirst')}
          onClick={() => setDeviceModalOpen(true)}
        >
          <Plus className="w-4 h-4" />
          {t('devices.new')}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600 mt-1">{t('devices.stats.total')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-theme-primary">{stats.webOn}</div>
          <div className="text-sm text-theme-primary mt-1">{t('devices.stats.webOn')}</div>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 relative min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('devices.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 input"
          />
        </div>
        <select
          className="input w-56"
          value={applicationFilter}
          onChange={(e) => setApplicationFilter(e.target.value)}
        >
          <option value="all">{t('devices.filterAllApps')}</option>
          {(applications ?? []).map((app) => (
            <option key={app.id} value={app.id}>
              {app.name}
            </option>
          ))}
        </select>
        <span className="text-gray-400">
          <Filter className="w-5 h-5" />
        </span>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="card p-12 text-center">
          <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm || applicationFilter !== 'all'
              ? t('devices.empty.none')
              : t('devices.empty.noneRegistered')}
          </h3>
          {!searchTerm && applicationFilter === 'all' && canCreateDevice && (
            <button type="button" className="btn btn-primary mt-4" onClick={() => setDeviceModalOpen(true)}>
              <Plus className="w-4 h-4" />
              {t('devices.new')}
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.device')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.application')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.type')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.template')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.platform')}</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.cloud')}</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('devices.headers.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredDevices.map((device) => {
                  const appId = device.applicationId ?? device.application_id
                  const devEui = device.devEui ?? device.dev_eui ?? ''
                  const wdDisabled = !String(devEui).trim()
                  const isCustomApi = isCustomApiKind(device.kind)
                  const wdOn = device.webDeviceEnabled ?? device.web_device_enabled
                  const wdTok = device.webDeviceTokenConfigured ?? device.web_device_token_configured
                  const cloud = buildWebCloudUi(device)
                  const canOpenUi = canOpenWebUi(device)
                  const canRefreshCloudInfo = isCustomApi && wdOn && wdTok
                  const tunnel = pick(device, 'webDeviceTunnelOnline', 'web_device_tunnel_online')
                  return (
                    <tr key={device.id} className="hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="icon-box" style={{ background: 'var(--theme-icon-box-background, color-mix(in srgb, var(--theme-primary) 15%, transparent))' , color: 'var(--theme-icon-box-text, var(--theme-primary))' }}>
                            <Cpu className="w-4 h-4" />
                          </div>
                          <div>
                            {canOpenUi ? (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!openDeviceWebUiWindow(devEui)) window.alert(t('devices.alerts.cannotOpenUi'))
                                }}
                                className="text-sm font-medium text-gray-900 hover:text-theme-primary hover:underline text-left"
                                title={t('devices.actions.openUi')}
                              >
                                {device.name}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setEditDevice(device)}
                                className="text-sm font-medium text-gray-900 hover:underline text-left"
                                title={webUiDisabledReason(device)}
                              >
                                {device.name}
                              </button>
                            )}
                            <div className="text-xs text-gray-500 font-mono">{device.devEui}</div>
                            <div className="text-[10px] text-gray-400 font-mono truncate max-w-[200px]" title={device.id}>
                              {device.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">
                        {appNameById[appId] ?? '—'}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">
                        {kindLabel(device.kind)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">
                        {device.templateName ?? device.template_name ?? '—'}
                      </td>
                      <td className="py-4 px-4">
                        {isCustomApi ? (
                          tunnel === true ? (
                            <span className="badge badge-success">{t('devices.platform.connected')}</span>
                          ) : tunnel === false ? (
                            <span className="badge badge-gray">{t('devices.platform.notConnected')}</span>
                          ) : (
                            <span className="badge badge-warning">{t('devices.platform.unavailable')}</span>
                          )
                        ) : (
                          <MqttEmqxPlataformaCell
                            device={device}
                            renderNonMqtt={() => <StatusBadge status={device.status} />}
                          />
                        )}
                      </td>
                      <td className="py-3 px-4 align-top max-w-xs">
                        <div className="flex flex-wrap gap-1">
                          {cloud.badges.map((b) => (
                            <span
                              key={b.key ?? b.label}
                              title={b.title || undefined}
                              className={clsx('badge', b.color, b.title && 'cursor-help')}
                            >
                              {b.label}
                            </span>
                          ))}
                        </div>
                        {cloud.detailLines.length > 0 && (
                          <ul className="text-[11px] text-gray-600 mt-1.5 space-y-0.5 leading-snug list-disc pl-3.5">
                            {cloud.detailLines.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right align-top">
                        <div className="flex flex-wrap gap-0.5 justify-end">
                          <button
                            type="button"
                            className={clsx(iconAct, 'hover:bg-gray-100')}
                            title={t('devices.actions.edit')}
                            onClick={() => setEditDevice(device)}
                          >
                            <Pencil className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            type="button"
                            className={clsx(iconAct, 'hover:bg-theme-secondary/10')}
                            disabled={!canRefreshCloudInfo || isFetching || wdDisabled}
                            title={
                              !wdOn
                                ? t('devices.uiDisabledReason.remoteOff')
                                : !wdTok
                                  ? t('devices.uiDisabledReason.tokenPending')
                                  : t('devices.actions.refreshCloud')
                            }
                            onClick={() => runRefreshCloudRow()}
                          >
                            <RefreshCw className="w-4 h-4 text-theme-secondary" />
                          </button>
                          <button
                            type="button"
                            className={clsx(iconAct, 'hover:bg-emerald-50')}
                            disabled={!isMqttDeviceKind(device.kind)}
                            title={
                              isMqttDeviceKind(device.kind)
                                ? t('devices.actions.openConsole')
                                : t('devices.alerts.consoleMqttOnly')
                            }
                            onClick={() => {
                              if (!openDeviceConsoleWindow(device.id)) {
                                window.alert(t('devices.alerts.cannotOpenConsole'))
                              }
                            }}
                          >
                            <Terminal className="w-4 h-4 text-emerald-600" />
                          </button>
                          <button
                            type="button"
                            className={clsx(iconAct, 'hover:bg-sky-50')}
                            disabled={!canOpenUi}
                            title={canOpenUi ? t('devices.actions.openUi') : webUiDisabledReason(device)}
                            onClick={() => {
                              if (!openDeviceWebUiWindow(devEui)) {
                                window.alert(t('devices.alerts.cannotOpenUi'))
                              }
                            }}
                          >
                            <ExternalLink className="w-4 h-4 text-sky-600" />
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              className={clsx(iconAct, 'hover:bg-red-50')}
                              disabled={deleteDevice.isPending}
                              title={t('devices.actions.delete')}
                              onClick={() => runDelete(device)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
